import {TONELADAS_HUMEDAS_POR_TRACTOMULA, TONELADAS_HUMEDAS_POR_VUELO, diasDeTransporte} from '@cnc/contracts';
import {useState} from 'react';
import {rutaInformeDeMineros} from '../api/mineros';
import {tr} from '../i18n/idioma';
import {fecha} from '../utilidades/formato';

// Por dia de la semana (0 = domingo); el transporte solo cae de lunes a viernes.
const DIAS = [
  ['Domingo', 'Sunday'],
  ['Lunes', 'Monday'],
  ['Martes', 'Tuesday'],
  ['Miércoles', 'Wednesday'],
  ['Jueves', 'Thursday'],
  ['Viernes', 'Friday'],
  ['Sábado', 'Saturday'],
] as const;
const nombreDelDia = (dia: string) => DIAS[new Date(`${dia}T00:00:00Z`).getUTCDay()]!;
const SIN_VIAJES = [0, 0, 0, 0, 0];
// Sin las flechas del navegador: Chrome las repite si el clic dura un cuarto de
// segundo y subia de a dos (Daniel, 3/11/2026). Los botones suman uno por clic.
const CAMPO =
  'w-12 rounded-md border border-linea px-2 py-1 text-right text-sm text-tinta [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
const BOTON = 'h-7 w-7 rounded-md border border-linea text-sm text-tinta hover:bg-fondo disabled:opacity-40';
const MAXIMO = 99;
const acotar = (n: number) => Math.min(MAXIMO, Math.max(0, Math.trunc(n) || 0));

function Contador({valor, etiqueta, fijar}: {valor: number; etiqueta: string; fijar: (n: number) => void}) {
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => fijar(acotar(valor - 1))}
        disabled={valor <= 0}
        aria-label={`${etiqueta}: ${tr('uno menos', 'one less')}`}
        className={BOTON}
      >
        −
      </button>
      <input
        type="number"
        min={0}
        max={MAXIMO}
        value={valor}
        aria-label={etiqueta}
        onChange={e => fijar(acotar(Number(e.target.value)))}
        className={CAMPO}
      />
      <button
        type="button"
        onClick={() => fijar(acotar(valor + 1))}
        disabled={valor >= MAXIMO}
        aria-label={`${etiqueta}: ${tr('uno más', 'one more')}`}
        className={BOTON}
      >
        +
      </button>
    </span>
  );
}

// Informe de inventario de concentrados (decision 0089): todo hasta el dia de
// corte (hoy, o el que se elija) y el equipo da los vuelos y tractomulas de los
// dias que quedan de esa semana (Daniel, 3/11/2026).
export function InformeDeInventarioMineros({hoy, onCerrar}: {hoy: string; onCerrar?: () => void}) {
  const [corte, setCorte] = useState(hoy);
  const [vuelos, setVuelos] = useState(SIN_VIAJES);
  const [tractomulas, setTractomulas] = useState(SIN_VIAJES);
  const semana = corte ? diasDeTransporte(corte) : [];
  const cambiar = (fijar: (f: (x: number[]) => number[]) => void, i: number) => (valor: number) =>
    fijar(lista => lista.map((x, j) => (j === i ? valor : x)));
  // Otro corte cambia los dias: los viajes escritos ya no corresponden.
  const cambiarCorte = (valor: string) => {
    setCorte(valor);
    setVuelos(SIN_VIAJES);
    setTractomulas(SIN_VIAJES);
  };

  return (
    <div className="space-y-3 rounded-lg border border-linea bg-white p-4" data-prueba="informe-mineros">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">
            {tr('Informe de inventario de concentrados', 'Concentrates inventory report')}
          </h2>
          <p className="text-xs text-apagado">
            {tr(
              `Excel con el concentrado por ubicación (planta, bodega y exportado) hasta el día de corte, la planeación de transporte de los días que quedan de esa semana y las exportaciones que aún no zarpan. Cada vuelo lleva ${TONELADAS_HUMEDAS_POR_VUELO} t húmedas de alto tenor y cada tractomula ${TONELADAS_HUMEDAS_POR_TRACTOMULA} t húmedas de bajo tenor; la humedad y el tenor salen del promedio histórico.`,
              `Excel with the concentrate by location (plant, warehouse and exported) up to the cut-off day, the transport plan for the remaining days of that week and the exports that have not sailed yet. Each flight carries ${TONELADAS_HUMEDAS_POR_VUELO} wet t of high grade and each truck ${TONELADAS_HUMEDAS_POR_TRACTOMULA} wet t of low grade; moisture and grade come from the historical average.`,
            )}
          </p>
        </div>
        {onCerrar && (
          <button
            type="button"
            onClick={onCerrar}
            aria-label={tr('Cerrar', 'Close')}
            className="text-apagado hover:text-tinta"
          >
            ×
          </button>
        )}
      </div>
      <label className="flex w-fit flex-col gap-1 text-xs text-apagado">
        {tr('Fecha de corte (incluye ese día)', 'Cut-off date (that day included)')}
        <input
          type="date"
          value={corte}
          onChange={e => cambiarCorte(e.target.value)}
          className="rounded-md border border-linea px-2 py-1 text-sm text-tinta"
        />
      </label>
      {corte && (
        <table className="text-sm">
          <thead>
            <tr className="text-xs text-apagado">
              <th className="pr-4 text-left font-normal">{tr('Día', 'Day')}</th>
              <th className="pr-4 text-right font-normal">{tr('Vuelos', 'Flights')}</th>
              <th className="text-right font-normal">{tr('Tractomulas', 'Trucks')}</th>
            </tr>
          </thead>
          <tbody>
            {semana.map((dia, i) => (
              <tr key={dia}>
                <td className="py-0.5 pr-4">
                  {tr(nombreDelDia(dia)[0], nombreDelDia(dia)[1])} {fecha(dia)}
                </td>
                <td className="py-0.5 pr-4 text-right">
                  <Contador
                    valor={vuelos[i]!}
                    etiqueta={tr(`Vuelos del ${nombreDelDia(dia)[0]}`, `Flights on ${nombreDelDia(dia)[1]}`)}
                    fijar={cambiar(setVuelos, i)}
                  />
                </td>
                <td className="py-0.5 text-right">
                  <Contador
                    valor={tractomulas[i]!}
                    etiqueta={tr(`Tractomulas del ${nombreDelDia(dia)[0]}`, `Trucks on ${nombreDelDia(dia)[1]}`)}
                    fijar={cambiar(setTractomulas, i)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <a
        href={
          corte
            ? rutaInformeDeMineros(corte, vuelos.slice(0, semana.length), tractomulas.slice(0, semana.length))
            : undefined
        }
        aria-disabled={!corte}
        className="inline-block rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white aria-disabled:opacity-50"
      >
        {tr('⬇ Descargar informe (Excel)', '⬇ Download report (Excel)')}
      </a>
    </div>
  );
}
